import { Button } from '@/components/ui/button.tsx';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs.tsx';
import { useCallback, useEffect, useMemo, useState } from 'react';
import type { Action, ActionStatus, ActionType } from "@ozon-ai/analysis/contracts";
import { ACTION_LABELS, STATUS_LABELS } from "@ozon-ai/analysis/contracts";
import { api } from '../api/client';
import { LoadingSpinner } from '../components/LoadingSpinner';
import { useTelegram } from '../context/TelegramContext';
import { ContentCard } from '../ui/ContentCard';
import { ErrorText, HintText, PageShell } from '../ui/PageShell';
import { StatusBadge } from '../ui/StatusBadge';

interface ActionWithPlan extends Action {
  planName?: string;
}

type ActionTabId = 'waiting' | 'inProgress' | 'done' | 'rejected';

const ACTION_TABS: { id: ActionTabId; label: string; statuses: ActionStatus[] }[] = [
  { id: 'waiting', label: 'Ожидают', statuses: ['proposed'] },
  { id: 'inProgress', label: 'В процессе', statuses: ['approved', 'executing', 'verifying'] },
  { id: 'done', label: 'Выполнено', statuses: ['succeeded', 'failed'] },
  { id: 'rejected', label: 'Отклонены', statuses: ['rejected'] },
];

const EMPTY_MESSAGES: Record<ActionTabId, string> = {
  waiting: 'Нет действий, ожидающих решения.',
  inProgress: 'Нет действий в процессе выполнения.',
  done: 'Нет выполненных действий.',
  rejected: 'Нет отклонённых действий.',
};

function actionsForTab(actions: ActionWithPlan[], tabId: ActionTabId) {
  const tab = ACTION_TABS.find((entry) => entry.id === tabId);
  if (!tab) {
    return [];
  }
  return actions.filter((action) => tab.statuses.includes(action.status));
}

export function ActionsPage() {
  const { webApp } = useTelegram();
  const [actions, setActions] = useState<ActionWithPlan[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<ActionTabId>('waiting');

  const load = useCallback(async () => {
    try {
      setError(null);
      const data = await api.get<ActionWithPlan[]>('/actions');
      setActions(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Не удалось загрузить действия');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const countsByTab = useMemo(() => {
    const counts: Record<ActionTabId, number> = {
      waiting: 0,
      inProgress: 0,
      done: 0,
      rejected: 0,
    };
    for (const tab of ACTION_TABS) {
      counts[tab.id] = actionsForTab(actions, tab.id).length;
    }
    return counts;
  }, [actions]);

  async function decide(actionId: string, approve: boolean) {
    setBusyId(actionId);
    try {
      if (approve) {
        await api.post('/actions/approve', { actionId });
      } else {
        await api.post('/actions/reject', { actionId, reason: 'Отклонено в Mini App' });
      }
      webApp?.HapticFeedback?.notificationOccurred(approve ? 'success' : 'warning');
      await load();
    } catch (err) {
      webApp?.HapticFeedback?.notificationOccurred('error');
      setError(err instanceof Error ? err.message : 'Ошибка');
    } finally {
      setBusyId(null);
    }
  }

  if (loading) return <LoadingSpinner />;

  return (
    <PageShell title="Аппрув действий">
      {error && <ErrorText>{error}</ErrorText>}

      <HintText className="mb-3">
        Запись в Ozon возможна только после одобрения. Без аппрува executor ничего не пишет.
      </HintText>

      <Tabs
        value={activeTab}
        onValueChange={(value) => setActiveTab(value as ActionTabId)}
        className="w-full"
      >
        <TabsList className="mb-3 h-auto w-full min-w-0">
          {ACTION_TABS.map((tab) => {
            const count = countsByTab[tab.id];
            return (
              <TabsTrigger key={tab.id} value={tab.id} className="flex-1 text-xs sm:text-sm">
                {tab.label}
                {count > 0 ? ` (${count})` : ''}
              </TabsTrigger>
            );
          })}
        </TabsList>

        {ACTION_TABS.map((tab) => (
          <TabsContent key={tab.id} value={tab.id} className="pt-1">
            {actionsForTab(actions, tab.id).length === 0 && (
              <ContentCard className="text-center">
                <p className="text-sm">{EMPTY_MESSAGES[tab.id]}</p>
              </ContentCard>
            )}

            {actionsForTab(actions, tab.id).map((action) => (
              <ActionCard
                key={action.id}
                action={action}
                busy={busyId === action.id}
                onApprove={
                  tab.id === 'waiting' ? () => void decide(action.id, true) : undefined
                }
                onReject={
                  tab.id === 'waiting' ? () => void decide(action.id, false) : undefined
                }
              />
            ))}
          </TabsContent>
        ))}
      </Tabs>
    </PageShell>
  );
}

function ActionCard({
  action,
  busy,
  onApprove,
  onReject,
}: {
  action: ActionWithPlan;
  busy?: boolean;
  onApprove?: () => void;
  onReject?: () => void;
}) {
  return (
    <ContentCard>
      <div className="flex items-center justify-between gap-2">
        <h2 className="text-base font-semibold">
          {ACTION_LABELS[action.type as ActionType] || action.type}
        </h2>
        <StatusBadge status={action.status}>
          {STATUS_LABELS[action.status] || action.status}
        </StatusBadge>
      </div>
      {action.planName && <HintText>{action.planName}</HintText>}
      <div className="mt-1 flex flex-col gap-1">
        <MetaRow label="SKU / offer" value={action.params.sku || action.params.offerId} />
        {action.params.currentPrice != null && (
          <MetaRow label="Текущая цена" value={`${action.params.currentPrice} ₽`} />
        )}
        {action.params.newPrice != null && (
          <MetaRow label="Новая цена" value={`${action.params.newPrice} ₽`} />
        )}
        {action.params.quantity != null && (
          <MetaRow label="Кол-во" value={String(action.params.quantity)} />
        )}
      </div>
      <p className="mt-1 text-sm">{action.params.reason}</p>
      {action.error && (
        <p className="mt-1 text-sm text-destructive">{action.error}</p>
      )}
      {onApprove && onReject && action.status === 'proposed' && (
        <div className="mt-3 flex gap-2">
          <Button type="button" className="flex-1" disabled={busy} onClick={onApprove}>
            Одобрить
          </Button>
          <Button type="button" variant="destructive" className="flex-1" disabled={busy} onClick={onReject}>
            Отклонить
          </Button>
        </div>
      )}
    </ContentCard>
  );
}

function MetaRow({ label, value }: { label: string; value: string | undefined }) {
  return (
    <div className="flex flex-col gap-0.5">
      <span className="text-xs text-muted-foreground">{label}</span>
      <span className="text-sm">{value || '—'}</span>
    </div>
  );
}
